/**
 * Gateway API client wiring.
 *
 * Configures the generated `OpenAPI` runtime config (from
 * `./gateway`, produced by `npm run gen:api:gateway`) with:
 *
 *   - the base URL of the F-300 Mirlai gateway (per ADR-006 the PWA
 *     talks to the gateway directly), driven by `VITE_GATEWAY_BASE_URL`;
 *   - a per-request bearer token resolver that reads the current JWT
 *     from local storage. The token is resolved at **call time** (not
 *     captured at init) so the value used reflects the most recent
 *     login / refresh.
 *
 * The codegen output (`./gateway/`) is committed to source — it does
 * not need to be rebuilt at install time. Re-run `npm run gen:api:gateway`
 * whenever `docs/api/mirlai-v1-openapi.json` changes (a CI drift check
 * will land in T-033).
 *
 * Call `initGatewayClient()` exactly once at app start, before any
 * service method is invoked. The orchestrator will wire this into
 * `src/main.tsx` after Wave A.
 *
 * Related: F-310 technical spec §§ 5.1, 5.2, 6.2; ADR-006 (auth topology).
 */
import { useAuth } from "./auth-store";
import { DEVICE_ID_HEADER } from "./deviceId";
import { OpenAPI } from "./gateway";

/**
 * Default gateway base URL used when `VITE_GATEWAY_BASE_URL` is not
 * set in the build environment. Matches `.env.example` dev default.
 */
const DEFAULT_GATEWAY_BASE_URL = "http://localhost:8200";

/**
 * Mount prefix of the Mirlai API sub-app on the gateway. The generated
 * `OpenAPI` paths (`/network/*`, `/devices/*`, …) are root-relative to the
 * sub-app, but the gateway mounts that sub-app at `/api/mirlai/v1`
 * (`gateway.main:app` — the add-on's `uvicorn` entrypoint). So the client's
 * `OpenAPI.BASE` MUST carry this prefix, otherwise every call lands on the
 * SPA catch-all (HTTP 200 + index.html) and the generated client parses HTML
 * as the response body. The Vite dev proxy forwards `/api/*` to the gateway,
 * so the same prefix works in `npm run dev` without extra wiring.
 */
const GATEWAY_API_PREFIX = "/api/mirlai/v1";

/**
 * Local-storage keys the resolver reads (via the `useAuth` store, which
 * writes through to these slots so non-React callers stay consistent):
 *
 *   - `mirlai_jwt`        — cloud-issued JWT (F-200, primary bearer).
 *   - `mirlai_device_id`  — anonymous account-less identity (F-348). Sent
 *                           as the `X-Mirlai-Device-Id` header, NOT a
 *                           bearer, on local-tier endpoints.
 */
const JWT_STORAGE_KEYS = ["mirlai_jwt", "mirlai_device_id"] as const;

/**
 * Resolve the current bearer token from the auth store.
 *
 * Only the cloud JWT (F-200-issued) is a bearer. Account-less Free
 * installs have no JWT — they identify via the `X-Mirlai-Device-Id`
 * header instead (`resolveLocalHeaders` below). `getActiveJwt()` returns
 * the cloud JWT or `null`.
 *
 * Returns an empty string if no token is set — the generated client
 * skips the `Authorization` header when the resolver yields `""`,
 * which is the desired behaviour for public + account-less endpoints.
 *
 * Async because `OpenAPI.TOKEN` has an async resolver signature;
 * `getActiveJwt()` itself is synchronous.
 */
async function resolveBearerToken(): Promise<string> {
  try {
    return useAuth.getState().getActiveJwt() ?? "";
  } catch {
    return "";
  }
}

/**
 * Resolve extra request headers. For account-less Free installs
 * (`mode === "local-only"`, no cloud JWT) we attach the anonymous
 * `device_id` as `X-Mirlai-Device-Id` so the gateway can authorize the
 * local-tier endpoints the Free tier needs (web-slice spec §7.2;
 * lane-A task 2). Cloud requests (JWT present) carry no device header —
 * the bearer identifies them. Returns `{}` otherwise so the client adds
 * nothing.
 */
async function resolveLocalHeaders(): Promise<Record<string, string>> {
  try {
    const { mode, getDeviceId } = useAuth.getState();
    const deviceId = getDeviceId();
    if (mode === "local-only" && deviceId) {
      return { [DEVICE_ID_HEADER]: deviceId };
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * Initialize the generated gateway API client with runtime config.
 *
 * Idempotent — safe to call more than once, though only the last call's
 * config wins. Typical placement is once at startup from `main.tsx`,
 * before the React tree mounts.
 *
 * @example
 *   // src/main.tsx
 *   import { initGatewayClient } from "./api/gateway-client";
 *   initGatewayClient();
 *   createRoot(container).render(<App />);
 */
export function initGatewayClient(): void {
  // Same-origin by default (issue #532). On the appliance the gateway serves
  // the SPA AND the API from the same host, and the kiosk loads the page from
  // `http://127.0.0.1:8200` (see `mirlai-kiosk.service`). A non-empty default
  // of `http://localhost:8200` made `OpenAPI.BASE` an ABSOLUTE `localhost` URL,
  // so every API call became cross-origin (`localhost` ≠ `127.0.0.1`) — the
  // kiosk's WPEWebKit then CORS-blocks the response (`Sec-Fetch-Site:
  // cross-site`, `isAccessControl=1`) and the WiFi scan hangs forever. A
  // RELATIVE base (empty string) always resolves against the page origin, so
  // it is same-origin no matter which host loaded the SPA. An explicit
  // `VITE_GATEWAY_BASE_URL` (a real cross-origin host) is still honoured; `||`
  // also collapses an empty-string build arg to relative. In `npm run dev` the
  // Vite proxy forwards `/api/*` to the gateway, so relative works there too.
  const baseUrl = import.meta.env.VITE_GATEWAY_BASE_URL || "";
  // Append the sub-app mount prefix (see `GATEWAY_API_PREFIX`). An empty
  // `baseUrl` yields the relative `/api/mirlai/v1`; an explicit host yields
  // `<host>/api/mirlai/v1`. The sub-app routes start with `/`, so no double
  // slash is introduced.
  OpenAPI.BASE = `${baseUrl}${GATEWAY_API_PREFIX}`;

  // Async resolver: fetched at call time, so subsequent logins are picked
  // up without re-initializing.
  OpenAPI.TOKEN = resolveBearerToken;

  // Account-less Free installs identify via the device_id header (F-348).
  // Resolved at call time so a pair/sign-out that flips `mode` is picked
  // up without re-initializing.
  OpenAPI.HEADERS = resolveLocalHeaders;

  // ADR-006: cross-origin requests use the bearer header — never rely on
  // cookies. Disabling credentials keeps the CORS preflight simple.
  OpenAPI.WITH_CREDENTIALS = false;
  OpenAPI.CREDENTIALS = "omit";
}

/**
 * Exposed for tests. Production code should not depend on this — use
 * the generated service classes (e.g. `DevicesService.getDevices()`)
 * which read `OpenAPI.TOKEN` automatically.
 */
export const __internal = {
  resolveBearerToken,
  resolveLocalHeaders,
  DEFAULT_GATEWAY_BASE_URL,
  GATEWAY_API_PREFIX,
  JWT_STORAGE_KEYS,
};
